/**
 * StepRibbon — tiny per-step media chips at the top of ContentPage.
 *
 * Each step in the chain (Origin → Change A → Change B → Contradiction →
 * Modern Pattern) gets a tiny preview chip rendered in its OWN era's medium.
 * Connecting arrows between chips visualize the Telephone Game arc as a
 * material-culture journey: stone → papyrus → vellum → newsprint → web.
 *
 * Click a chip to jump to that step. Active chip glows with the era's primary.
 *
 * This is the chain-as-visual-journey teaching surface. A user can see at a
 * glance: "This is a story that started carved in stone and ended on screens."
 */
import { getEraForYear } from '../lib/eraGrammar';
import { getEraMedium } from '../lib/eraMedia';

const STEP_LABELS = {
  origin: 'ORIGIN',
  change_a: 'CHANGE A',
  change_b: 'CHANGE B',
  contradiction: 'CONTRADICTION',
  modern_pattern: 'MODERN',
};

function chipBackgroundFor(eraId) {
  const m = getEraMedium(eraId);
  if (eraId === 'era9') return 'rgba(47,138,146, 0.06)';
  return m.background;
}

function chipInkFor(eraId) {
  if (eraId === 'era9') return '#FFFFFF';
  return getEraMedium(eraId).ink;
}

function ChipMiniature({ eraId }) {
  // Tiny visual cue specific to each era's medium — readable at 24x14
  const m = getEraMedium(eraId);
  if (eraId === 'era1' || eraId === 'era0') {
    // Stone — chiseled hash marks
    return (
      <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
        <line x1="3" y1="4" x2="9" y2="4" stroke={m.ink} strokeWidth="1" />
        <line x1="11" y1="4" x2="20" y2="4" stroke={m.ink} strokeWidth="1" />
        <line x1="3" y1="8" x2="14" y2="8" stroke={m.ink} strokeWidth="1" />
        <line x1="3" y1="11" x2="18" y2="11" stroke={m.ink} strokeWidth="1" />
      </svg>
    );
  }
  if (eraId === 'era2') {
    // Papyrus lines
    return (
      <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
        <line x1="3" y1="4" x2="20" y2="4" stroke={m.ink} strokeWidth="0.5" />
        <line x1="3" y1="7" x2="17" y2="7" stroke={m.ink} strokeWidth="0.5" />
        <line x1="3" y1="10" x2="20" y2="10" stroke={m.ink} strokeWidth="0.5" />
      </svg>
    );
  }
  if (eraId === 'era3' || eraId === 'era4') {
    // Vellum / printed page — initial cap with line
    return (
      <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
        <text x="2" y="11" style={{ fontFamily: "'Uncial Antiqua', serif", fontSize: 10, fill: '#B22222' }}>L</text>
        <line x1="11" y1="6" x2="22" y2="6" stroke={m.ink} strokeWidth="0.5" />
        <line x1="11" y1="9" x2="20" y2="9" stroke={m.ink} strokeWidth="0.5" />
      </svg>
    );
  }
  if (eraId === 'era5') {
    // Newsprint — column rules
    return (
      <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
        <line x1="8" y1="2" x2="8" y2="12" stroke={m.ink} strokeWidth="0.4" />
        <line x1="16" y1="2" x2="16" y2="12" stroke={m.ink} strokeWidth="0.4" />
        <rect x="3" y="3" width="4" height="8" fill={m.ink} opacity="0.3" />
        <rect x="9" y="3" width="6" height="8" fill={m.ink} opacity="0.2" />
        <rect x="17" y="3" width="4" height="8" fill={m.ink} opacity="0.3" />
      </svg>
    );
  }
  if (eraId === 'era6' || eraId === 'era7') {
    // Typewritten — monospace marks
    return (
      <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
        <text x="2" y="6" style={{ fontFamily: "'Special Elite', monospace", fontSize: 5, fill: m.ink }}>TSOGN</text>
        <text x="2" y="12" style={{ fontFamily: "'Special Elite', monospace", fontSize: 5, fill: m.ink, opacity: 0.7 }}>1925</text>
      </svg>
    );
  }
  if (eraId === 'era8') {
    // CRT terminal
    return (
      <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
        <text x="2" y="9" style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 6, fill: '#7AFF7A' }}>{'>_'}</text>
        <line x1="3" y1="11" x2="20" y2="11" stroke="#7AFF7A" strokeWidth="0.4" opacity="0.6" />
      </svg>
    );
  }
  // era9 web — clean wave
  return (
    <svg viewBox="0 0 24 14" width="24" height="14" aria-hidden="true">
      <path d="M 2 7 Q 8 3, 14 7 T 22 7" stroke="#2F8A92" strokeWidth="0.7" fill="none" />
    </svg>
  );
}

export default function StepRibbon({ chain, currentStepKey, onStepClick }) {
  if (!chain) return null;

  // Build step metadata array; gate eraId off each step's year. Falls back to
  // origin year when a step's year is missing.
  const stepKeys = ['origin', 'change_a', 'change_b', 'contradiction', 'modern_pattern'];
  const fallbackYear = chain.origin?.year || 0;
  const steps = stepKeys.map((k) => {
    const data = chain[k];
    const year = data?.year ?? fallbackYear;
    const era = getEraForYear(year);
    return { key: k, year, eraId: era.id, eraLabel: era.label };
  });

  return (
    <div
      className="step-ribbon"
      data-testid="step-ribbon"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        overflowX: 'auto',
      }}
      aria-label="Chain progression — material culture across eras"
    >
      {steps.map((s, i) => {
        const isActive = s.key === currentStepKey;
        const bg = chipBackgroundFor(s.eraId);
        const ink = chipInkFor(s.eraId);
        return (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              data-testid={`step-ribbon-chip-${s.key}`}
              onClick={() => onStepClick && onStepClick(s.key)}
              title={`${STEP_LABELS[s.key]} · ${s.eraLabel} · ${s.year < 0 ? `${Math.abs(s.year)} BCE` : `${s.year} CE`}`}
              style={{
                background: bg,
                color: ink,
                border: isActive ? `1.5px solid ${ink}` : `1px solid ${ink}40`,
                padding: '4px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                minWidth: 64,
                transition: 'all 220ms ease',
                boxShadow: isActive ? `0 0 0 2px ${ink}33, 0 2px 6px ${ink}22` : 'none',
                transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >
              <ChipMiniature eraId={s.eraId} />
              <span style={{ fontSize: 7, letterSpacing: '0.14em', opacity: 0.85 }}>
                {STEP_LABELS[s.key]}
              </span>
            </button>
            {i < steps.length - 1 && (
              <span
                aria-hidden="true"
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  color: 'rgba(212,168,74, 0.45)',
                  fontSize: 12,
                  fontWeight: 300,
                }}
              >
                →
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
